import { AlertTriangle, Database, Network, Store, Truck } from "lucide-react"
import { marketplaceStateLabel } from "@nova-ai/shared"
import type { OrderDetail } from "../../services/use-orders"
import { marketplaceChipKind } from "../../lib/marketplace"
import { omsChipKind, omsStateLabel } from "../../lib/oms"
import { formatDateTime } from "../../lib/format-date"
import { StateChip } from "../../routes/orders.index"
import { StatusBadge, type BadgeTone } from "../ui/status-badge"
import { CopyValue } from "../ui/copy-value"
import { cn } from "../../lib/utils"
import { journeyFlag, preferredTrackingUrl } from "./order-detail-model"
import { OrderExternalLink } from "./order-external-link"
import { MagentoAttempts } from "./magento-attempts"
import { ReplacedFromNote } from "./replaced-from-note"

const badgeTones = {
  good: "success",
  warn: "warning",
  bad: "destructive",
  info: "info",
  muted: "muted",
} satisfies Record<string, BadgeTone>

export function OrderJourney({ data }: { data: OrderDetail }) {
  const trackingUrl = preferredTrackingUrl(data)
  const packed = data.oms.shipments.flatMap((shipment) =>
    shipment.packedAt ? [formatDateTime(shipment.packedAt)] : []
  )
  const rows = [
    {
      key: "marketplace",
      name: data.platformLabel,
      role: "Marketplace",
      icon: Store,
      reference: data.orderId,
      status: marketplaceStateLabel(data.state),
      tone: marketplaceChipKind(data.state) ?? "muted",
      meta: data.marketplaceTracking
        ? `Tracking sent: ${[data.marketplaceTracking.carrier, data.marketplaceTracking.trackingCode].filter(Boolean).join(" · ")}`
        : "No tracking sent yet",
    },
    {
      key: "baselinker",
      name: "Baselinker",
      role: "Order routing",
      icon: Network,
      reference: data.baselinker.orderId,
      status: data.baselinker.state,
      tone: "info",
      meta: data.baselinker.syncedAt
        ? `Synced ${formatDateTime(data.baselinker.syncedAt)}`
        : "Not synced",
    },
    {
      key: "magento",
      name: "Magento",
      role: "Commerce order",
      icon: Database,
      reference: data.magento.orderId,
      status: data.magento.orderStatus ?? data.magento.state,
      tone: magentoTone(data),
      meta: data.magento.syncedAt
        ? `Synced ${formatDateTime(data.magento.syncedAt)}`
        : "Not synced",
    },
    {
      key: "oms",
      name: "OMS",
      role: "Warehouse & delivery",
      icon: Truck,
      reference: data.oms.orderId,
      status: data.oms.state ? omsStateLabel(data.oms.state) : null,
      tone: data.oms.state ? omsChipKind(data.oms.state) : "muted",
      meta: [
        data.oms.shipments.length
          ? `${data.oms.shipments.length} shipment${data.oms.shipments.length === 1 ? "" : "s"}`
          : "No shipment yet",
        ...packed.map((time) => `packed ${time}`),
        data.oms.trackingCode,
      ]
        .filter(Boolean)
        .join(" · "),
    },
  ] as const
  return (
    <section aria-label="Journey" className="rounded-xl border border-border bg-card p-4 sm:p-5">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Journey</h2>

          <p className="mt-1 text-xs text-muted-foreground">
            Marketplace → Baselinker → Magento → OMS
          </p>
        </div>

        {trackingUrl ? <OrderExternalLink url={trackingUrl} label="Track parcel" /> : null}
      </header>

      <ol className="mt-4 divide-y divide-border">
        {rows.map((row, index) => {
          const flag = journeyFlag(data, row.key)
          const tone = flag?.kind ?? row.tone
          const Icon = flag ? AlertTriangle : row.icon
          return (
            <li
              key={row.key}
              className="relative grid grid-cols-[32px_minmax(0,1fr)] gap-x-3 gap-y-2 py-4 md:grid-cols-[32px_minmax(0,1fr)_minmax(0,1fr)_auto]"
            >
              {index < rows.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="absolute top-12 bottom-[-16px] left-4 w-px bg-border"
                />
              ) : null}

              <span
                className={cn(
                  "relative flex size-8 items-center justify-center rounded-full",
                  {
                    good: "bg-success text-success-foreground",
                    warn: "bg-warning text-warning-foreground",
                    bad: "bg-destructive text-destructive-foreground",
                    info: "bg-info text-info-foreground",
                    muted: "bg-muted text-muted-foreground",
                  }[tone]
                )}
              >
                <Icon className="size-4" />
              </span>

              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <h3 className="text-sm font-semibold">{row.name}</h3>

                  <span className="text-xs text-muted-foreground">{row.role}</span>
                </div>

                <div className="mt-1 flex flex-wrap items-center gap-2">
                  {row.status ? (
                    <StatusBadge tone={badgeTones[row.tone]}>{row.status}</StatusBadge>
                  ) : (
                    <span className="text-xs text-muted-foreground">No record</span>
                  )}

                  {flag ? (
                    <StateChip kind={flag.kind}>
                      <AlertTriangle className="mr-1 size-3" />

                      {flag.label}
                    </StateChip>
                  ) : null}
                </div>
              </div>

              <div className="col-start-2 min-w-0 md:col-start-3">
                {row.reference ? (
                  <CopyValue value={row.reference} className="font-mono text-xs" />
                ) : (
                  <span className="text-xs text-muted-foreground">No reference yet</span>
                )}

                <p className="mt-1 text-xs text-muted-foreground">{row.meta}</p>
              </div>

              <OrderExternalLink
                url={data.urls[row.key]}
                label={`Open ${row.name}`}
                className="col-start-2 w-full self-start rounded-full border border-border px-3 text-foreground md:col-start-4 md:w-auto"
              />
            </li>
          )
        })}
      </ol>

      <details open={data.magento.state === "ambiguous"} className="border-t border-border pt-2">
        <summary className="min-h-11 cursor-pointer content-center text-sm font-medium focus-visible:outline-2 focus-visible:outline-ring">
          <span>Magento attempts</span>

          <span className="ml-2 text-muted-foreground">{data.magento.attemptHistory.length}</span>

          {data.magento.state === "ambiguous" ? (
            <span className="ml-2 inline-flex">
              <StateChip kind="warn">Choose one</StateChip>
            </span>
          ) : null}
        </summary>

        {data.magento.replacedFrom ? (
          <ReplacedFromNote replacedFrom={data.magento.replacedFrom} state={data.magento.state} />
        ) : null}

        <MagentoAttempts data={data} />
      </details>
    </section>
  )
}

function magentoTone(data: OrderDetail) {
  if (data.magento.state === "error") {
    return "bad"
  }
  if (["ambiguous", "not_found"].includes(data.magento.state ?? "")) {
    return "warn"
  }
  return data.magento.orderId ? "good" : "muted"
}
